import HeaderPortal from "components/portal/header-portal";
import { FormLabel } from "components/form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form";
import { FormColumns } from "components/form";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import React from "react";
import { ticketSchema } from "./schema";
import { useHotkeys } from "react-hotkeys-hook";
import SelectStatus from "components/select/status";
import SelectType from "components/select/type";
import SelectPriority from "components/select/priority";
import { DatePicker } from "components/date-picker-2";
import TiptapEditor from "components/tiptap-editor";
import { useTicketForm } from "./useTicketForm";
import MultiFileUpload from "ui/components/multi-file-upload";

const TicketForm = ({ ticketId }) => {

  const { form, requiredFields, handleCreate, handleRead, handleUpdate } =
    useTicketForm({ ticketId });

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={ticketId || "+"}
      schema={ticketSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(false);
              createItem().then(handleClose);
            }
          },
          { preventDefault: true }
        );

        useHotkeys(
          "ctrl+shift+s, cmd+shift+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(true);
              createItem();
            }
          },
          { preventDefault: true }
        );
        return (
          <>
            <div className="space-y-6">
              {form.error && (
                <div className="text-red-600 bg-red-50 p-2 rounded">
                  {form.error.message || form.error}
                </div>
              )}
              {/* Title */}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <PermissionControlledInput
                        rule={isNewItem ? "create:*" : "update:*"}
                        collection="tickets"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Title
                        </FormLabel>

                        <Input {...field} />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="tickets"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Description
                      </FormLabel>
                      <TiptapEditor {...field} className="min-h-32" />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Status */}
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:none" : "update:*"}
                      collection="tickets"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Status
                      </FormLabel>
                      <SelectStatus
                        collection="tickets"
                        onValueChange={(val) => field.onChange(val)}
                        {...field}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormColumns>
                {/* Type */}
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem required={requiredFields.includes(field.name)}>
                      <PermissionControlledInput
                        rule={isNewItem ? "create:*" : "update:*"}
                        collection="tickets"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Type
                        </FormLabel>
                        <SelectType
                          collection="tickets"
                          onValueChange={(val) => field.onChange(val)}
                          {...field}
                        />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Priority */}
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={isNewItem ? "create:*" : "update:*"}
                        collection="tickets"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Priority
                        </FormLabel>
                        <SelectPriority
                          collection="tickets"
                          onValueChange={(val) => field.onChange(val)}
                          {...field}
                        />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormColumns>

              {/* Attachment */}
              <FormField
                control={form.control}
                name="attachments"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="tickets"
                      type="form"
                      field={field.name}
                      showWhenDisabled={false}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Upload Files
                      </FormLabel>
                      <MultiFileUpload
                        value={field.value || []}
                        onChange={field.onChange}
                        accept="image/*"
                        maxFiles={10}
                        className="w-full"
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Due Date */}
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:*"}
                      collection="tickets"
                      field={field.name}
                      type="form"
                      showWhenDisabled={false}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Due Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Response */}
              {/* <FormField
                                control={form.control}
                                name="response"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:none" : "update:*"}
                                            collection="tickets"
                                            type="form"
                                            field={field.name}
                                        >
                                            <FormLabel>Response</FormLabel>
                                            <Textarea {...field} className="min-h-32" />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> */}

              {/* Portal buttons similar to TimesheetLog */}
              <HeaderPortal>
                {isNewItem ? (
                  <ButtonWithLoading
                    variant="default"
                    isLoading={isCreating}
                    onClick={createItem}
                  >
                    Save
                  </ButtonWithLoading>
                ) : (
                  <ButtonWithLoading
                    variant="default"
                    isLoading={isUpdating}
                    disabled={!isDirty}
                    onClick={updateItem}
                  >
                    Update
                  </ButtonWithLoading>
                )}
              </HeaderPortal>
            </div>
          </>
        );
      }}
    </Form>
  );
};

export default TicketForm;
